/**
 * The room browser: the list a player picks a room off, before they are in a match.
 *
 * Built as a **touch list, not a table**. It is the first thing a mobile player sees, so every
 * row is a full-width card with a thumb-sized Play button rather than a dense grid of columns —
 * the operator console already has the table, and it is for a different reader on a different
 * device.
 *
 * The counts move, so the list polls. `rooms.list` is served from a process cache on the server
 * (`services/rooms/lobby.ts`, `LOBBY_CACHE_TTL_MS` 3 s), so a poll costs a cached read rather
 * than a fan-out to every host — which is what makes it safe for every phone on the landing page
 * to be doing this at once.
 *
 * **A full room's button is disabled and the reason is drawn next to it**, but that is a courtesy
 * and never the enforcement: the id this list hands to `match.join` is a request, and the server
 * decides the seat against live occupancy. A row three seconds stale cannot get anybody into a
 * room that is full.
 *
 * A room with ACCESS RULES — a skill band, a device restriction — is the same arrangement one
 * step further. The rules are drawn as small badges beside the name, and a room the viewer does
 * not meet is disabled with the reason in the note line, in the *server's own words*: the
 * sentence comes from `checkRoomAccess`, which is also what `match.join` throws if a client
 * presses anyway. Nothing here decides anything — a viewer's eligibility is computed on the
 * server, from a rating and a device the client never sends.
 *
 * ── Why the list does not move ──
 *
 * A refresh must be *invisible until a number changes*. On a phone the landing page is centred in
 * a flex column, so anything that changes this list's height moves the Play button that is
 * already under somebody's thumb. Three rules, and they are what the layout is built out of:
 *
 * 1. **A row is a fixed height.** `ROW_CLASS` is `h-*`, not `min-h-*`, and every line inside it
 *    is reserved whether or not it has anything in it — including the "full" note, which used to
 *    appear and disappear and take 20px of the page with it. A room filling up must not reflow
 *    the rooms under it.
 * 2. **Nothing inside a row wraps.** Every line truncates, so "1 player · 0 bots" growing into
 *    "12 players · 4 bots" can change the text and never the height.
 * 3. **The last good list stays on screen.** The loading line is drawn only before the first
 *    successful answer; after that a refetch — and a *failed* refetch — leaves the rows exactly
 *    where they were. A list that swapped itself for a spinner would jank at any interval at all.
 *
 * ── Why it is still polling ──
 *
 * Deliberately, and this is the conclusion rather than a thing left undone. The query is cached
 * and rate-limited on the server, a row is five short fields, and the answer a lobby needs is
 * "roughly who is in there" rather than a live feed. SSE would be the option worth taking if this
 * ever wanted to be exact; a game socket is not (it costs a worker connection per *browsing*
 * player, and connections are the measured bottleneck). What was actually wrong was the jumping
 * and the fact that a phone in somebody's pocket kept doing it — so the interval doubled, and it
 * stops entirely while the app is not being looked at.
 */

import {
  isLobbyRoomJoinable,
  type LobbyRoomStatus,
  ROOM_MODE_LABELS,
  type RoomAccessBadge,
  roomAccessBadges,
} from '@chompdome/shared/constants';
import type { RoomAccessRules, RoomMode } from '@chompdome/shared/types';
import { Button } from '@/components/ui/button';
import { useAppVisible } from '@/hooks/use-app-visible';
import { trpc } from '@/lib/trpc';

/**
 * A lobby, not a dashboard.
 *
 * Ten seconds against the server cache's three: a count that is at worst one interval stale is
 * still "who is in there", and the room is re-decided against live occupancy on the way in
 * anyway. Halving the request rate matters more on the surface this is read on.
 */
const POLL_INTERVAL_MS = 10_000;

/** What each status says on the row, and how it is coloured. */
const STATUS_LABELS: Record<LobbyRoomStatus, string> = {
  idle: 'Empty',
  available: 'Open',
  filling: 'Filling up',
  full: 'Full',
  /**
   * Two words, because this badge shares the name's line with the mode badge and the access
   * badges and the NAME is what a player is choosing between. "Round in progress" is the whole
   * sentence and it is already on the row's third line ("A round is under way — you join the
   * next one"), where there is room for it; a badge only has to be the label.
   */
  round: 'In round',
};

const STATUS_TONES: Record<LobbyRoomStatus, string> = {
  idle: 'bg-muted text-muted-foreground',
  available: 'bg-primary/15 text-primary',
  filling: 'bg-accent/15 text-accent',
  full: 'bg-destructive/15 text-destructive',
  // The accent, not the destructive tone: a round under way is a thing to know about and not a
  // refusal. The button under it still presses.
  round: 'bg-accent/15 text-accent',
};

/**
 * What a room mid-round owes the reader, in the third line.
 *
 * The seats are real — the player takes one, in the queue for the next round — so the sentence
 * is about *when* rather than about capacity, and it is drawn in the ordinary muted ink rather
 * than in the destructive one the refusals use. A row's third line is the reason beside the
 * button, and "you are joining the next round" is a reason.
 */
const ROUND_NOTE = 'A round is under way — you join the next one.';

/**
 * The row box, as one constant, because its height is the thing being asserted about.
 *
 * `h-[5.5rem]` is three reserved lines plus the card's padding: the name, the counts and the
 * note. Nothing in a row may change it.
 */
const ROW_CLASS = 'flex h-[5.5rem] items-center gap-3 rounded-xl border border-border bg-card p-3';

/**
 * The counts, as one string.
 *
 * Bots are named separately from players because that is the decision the card is about: "18
 * players" made of two humans and sixteen bots is a different room from one made of eighteen
 * humans, and only one of them is a game worth walking into.
 */
function occupancy(room: { humanCount: number; botCount: number; capacity: number }): string {
  const players = `${room.humanCount} ${room.humanCount === 1 ? 'player' : 'players'}`;
  const bots = `${room.botCount} ${room.botCount === 1 ? 'bot' : 'bots'}`;
  return `${players} · ${bots} · ${room.capacity} seats`;
}

/**
 * Whether the app is being looked at.
 *
 * `@/native/platform` because the shell and the browser answer it differently and this component
 * must not know which it is in: a Capacitor app reports being backgrounded as `appStateChange`
 * and a tab reports it as `visibilitychange`.
 */
/**
 * One rule, as a chip.
 *
 * The glyph is decoration and the text carries the meaning, so the whole chip takes the badge's
 * full sentence as its accessible name — a screen reader that read "★ Expert+" out of context
 * would be reading a decoration and a fragment.
 */
function AccessBadge({ badge }: { badge: RoomAccessBadge }) {
  return (
    <span
      // `role="img"` is what lets the whole chip carry one accessible name: a bare `<span>` has
      // no role for `aria-label` to attach to, and the glyph and the abbreviation read as
      // nothing useful on their own.
      role="img"
      className="inline-flex shrink-0 items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-xs font-semibold text-muted-foreground"
      title={badge.label}
      aria-label={badge.label}
    >
      <span aria-hidden="true">{badge.glyph}</span>
      {badge.text}
    </span>
  );
}

/** The rules a room publishes, in `roomAccessBadges`' order. Nothing at all for an open room. */
function AccessBadges({ access }: { access: RoomAccessRules }) {
  return (
    <>
      {roomAccessBadges(access).map((badge) => (
        <AccessBadge key={badge.label} badge={badge} />
      ))}
    </>
  );
}

/**
 * What KIND of game this is — and nothing at all for a classic room.
 *
 * Drawn like an access badge and deliberately not *as* one: a mode is not a door rule, nothing
 * is ever refused for it, and `roomAccessBadges` answers a question about who may enter. What it
 * shares is the shape and the place — the name's line, because the row is a fixed height and a
 * room acquiring a mode must not push the rooms under it down the page.
 *
 * Classic gets nothing, on `devices: 'any'`'s rule: a badge on every row is a badge that says
 * nothing, and the game a player already knows about is the one that needs no label.
 */
function ModeBadge({ mode }: { mode: RoomMode }) {
  if (mode === 'classic') return null;
  return (
    <span
      role="img"
      className="inline-flex shrink-0 items-center gap-1 rounded-full bg-primary/15 px-2 py-0.5 text-xs font-semibold text-primary"
      title={ROOM_MODE_LABELS[mode]}
      aria-label={ROOM_MODE_LABELS[mode]}
    >
      <span aria-hidden="true">◈</span>
      {ROOM_MODE_LABELS[mode]}
    </span>
  );
}

export interface RoomListProps {
  /** Called with the room id the player picked. The caller owns the session and the navigation. */
  onPlay: (roomId: string) => void;
  /**
   * Called with the room id the reader wants to WATCH.
   *
   * A second, quieter button on every row that has something to watch. It is offered beside Play
   * rather than instead of it because the two are different questions — "where do I want to
   * play" and "what is going on in there" — and the second one is what the room browser was
   * already being read for.
   */
  onWatch: (roomId: string) => void;
  /** True while a guest account is being minted, so a second tap cannot start a second one. */
  busy?: boolean;
}

export function RoomList({ onPlay, onWatch, busy = false }: RoomListProps) {
  const visible = useAppVisible();
  const rooms = trpc.rooms.list.useQuery(undefined, {
    // `false` is React Query's "no interval": a backgrounded phone polls nothing at all, and
    // picks the timer back up on the visibility change rather than on a schedule.
    refetchInterval: visible ? POLL_INTERVAL_MS : false,
    refetchIntervalInBackground: false,
  });

  // Two things end in the same place, and it is nothing at all rather than an empty box or an
  // error: a deployment that declares no rooms — a supported configuration, and what a fresh
  // install is — and a list that would not load. Quick play is then the whole screen, exactly as
  // it was before there was a room browser: a browser that failed must never be what stops
  // somebody playing.
  //
  // `data` is read BEFORE `error`, which is the whole of rule 3 above: React Query keeps the last
  // successful answer through a failed refetch, so a poll that times out leaves the rows where
  // they are instead of collapsing the page and putting them back ten seconds later.
  const list = rooms.data;
  if (list === undefined) {
    return rooms.isLoading ? (
      <p className="mt-8 text-sm text-muted-foreground">Looking for rooms…</p>
    ) : null;
  }
  if (list.length === 0) return null;

  return (
    <section className="mt-8 w-full text-left" aria-labelledby="room-list-heading">
      <h2 id="room-list-heading" className="text-sm font-semibold text-muted-foreground">
        Or pick a room
      </h2>

      <ul className="mt-3 flex flex-col gap-2">
        {list.map((room) => {
          // Two different refusals, and the room has to say which. A full room may have a seat
          // in ten seconds; a room whose rules this viewer does not meet will not, so the rules
          // are named first when both apply.
          const seated = isLobbyRoomJoinable(room.status);
          // Three things the line can be, and only two of them are refusals. A round under way
          // is the third: the room takes the player, and what it owes them is the sentence
          // saying which game the seat is in.
          const note = room.eligible
            ? seated
              ? room.status === 'round'
                ? ROUND_NOTE
                : ''
              : 'Every seat is taken.'
            : (room.ineligibleReason ?? 'You cannot enter this room.');
          const refusal = !room.eligible || !seated;
          const joinable = seated && room.eligible;
          // Watching asks a different question of the same row: not "is there a seat" — a
          // spectator takes none — but "is anything running", because a spectator deliberately
          // cannot wake an idle declaration (see `placeSpectator`). The access rules still
          // apply, so a room this reader may not enter is a room they may not watch either.
          const watchable = room.live && room.eligible;
          return (
            <li key={room.id}>
              <div className={ROW_CLASS}>
                <div className="min-w-0 flex-1">
                  <p className="flex items-center gap-2">
                    {/* **THE NAME NEVER SHRINKS TO NOTHING.** It is the one thing on this row a
                        player is actually choosing between, and it was the only flexible item on
                        a line of `shrink-0` badges — so a deathmatch room mid-round, which wears
                        two of them, rendered as "Dea…". `min-w-` is a floor under the truncation
                        rather than a fixed width: the badges still take what they need, and what
                        gives way first is now the end of a long name rather than all of it. */}
                    <span className="min-w-[6rem] truncate font-semibold">{room.name}</span>
                    <span
                      className={`inline-flex shrink-0 items-center rounded-full px-2 py-0.5 text-xs font-semibold ${STATUS_TONES[room.status]}`}
                    >
                      {STATUS_LABELS[room.status]}
                    </span>
                    {/* On the name's line rather than a line of their own: the row is a fixed
                        height (rule 1 above), and a room acquiring a rule must not push the
                        rooms under it down the page. */}
                    <ModeBadge mode={room.mode} />
                    <AccessBadges access={room.access} />
                  </p>
                  <p className="mt-0.5 truncate text-sm tabular-nums text-muted-foreground">
                    {occupancy(room)}
                  </p>
                  {/*
                   * The reason, beside the button that will not press. A player who can see
                   * "Full" still deserves to be told what it costs them — and the line is drawn
                   * empty rather than omitted, so a room that fills up while it is being read
                   * changes its own text and moves nothing under it.
                   *
                   * An access refusal is the server's own sentence, so a player who presses
                   * anyway is told the same thing twice rather than two different things. It
                   * truncates for the height rule and carries the whole of itself in `title`.
                   */}
                  <p
                    className={`mt-0.5 h-5 truncate text-sm ${refusal ? 'text-destructive' : 'text-muted-foreground'}`}
                    title={note}
                  >
                    {note}
                  </p>
                </div>

                {/* Side by side rather than stacked: the row is a fixed 5.5rem and its padding
                    leaves 4rem of content height, which one `lg` button fills. */}
                <div className="flex shrink-0 items-center gap-2">
                  <Button
                    size="lg"
                    className="px-5"
                    // The visible word is the same on every row, so the name a screen reader (and
                    // a route test) reads has to carry the room it belongs to.
                    aria-label={`Play ${room.name}`}
                    disabled={!joinable || busy}
                    onClick={() => onPlay(room.id)}
                  >
                    Play
                  </Button>
                  {/* Always rendered, never conditional: the row is a fixed height (rule 1
                      above), so a room that stops running must change this button's state and
                      not the page's layout. */}
                  <Button
                    size="sm"
                    variant="secondary"
                    className="px-3"
                    aria-label={`Watch ${room.name}`}
                    disabled={!watchable || busy}
                    onClick={() => onWatch(room.id)}
                  >
                    Watch
                  </Button>
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
